import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Badge, Button, Card, EmptyState, ErrorAlert, Field } from '../components/ui';

type Any = Record<string, any>;

export function NieboGuardMusicSection() {
  const [profiles, setProfiles] = useState<Any[]>([]);
  const [tracks, setTracks] = useState<Any[]>([]);
  const [generations, setGenerations] = useState<Any[]>([]);
  const [profileId, setProfileId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [settings, setSettings] = useState<Any | null>(null);
  const [audioSettings, setAudioSettings] = useState<Any | null>(null);
  const [playlists, setPlaylists] = useState<Any[]>([]);
  const [playlistName, setPlaylistName] = useState('');

  const load = useCallback(async () => {
    const [profilesResult, tracksResult, generationsResult, settingsResult, playlistResult] = await Promise.all([
      api.nieboguard.music.profiles(),
      api.nieboguard.music.tracks(),
      api.nieboguard.music.generations(),
      api.nieboguard.settings(),
      api.nieboguard.music.playlists(),
    ]);
    setProfiles(profilesResult.profiles);
    setTracks(tracksResult.tracks);
    setGenerations(generationsResult.generations);
    setSettings(settingsResult.settings.music ?? null);
    setAudioSettings(settingsResult.settings.audio ?? null);
    setPlaylists(playlistResult.playlists);
    setProfileId((current) => current || profilesResult.profiles[0]?.id || '');
  }, []);

  useEffect(() => { load().catch(setError); }, [load]);

  const run = async (key: string, fn: () => Promise<unknown>, message: string) => {
    setBusy(key); setError(null); setNotice(null);
    try { await fn(); setNotice(message); await load(); }
    catch (err) { setError(err); }
    finally { setBusy(null); }
  };

  return (
    <>
      {error ? <ErrorAlert error={error} /> : null}
      {notice ? <Card><div className="nb-stat-note">{notice}</div></Card> : null}

      <Card title="Music Studio — NG TV" actions={<Badge tone={settings?.enabled ? 'success' : 'warning'}>{settings?.enabled ? 'generowanie włączone' : 'generowanie wyłączone'}</Badge>}>
        <p className="nb-stat-note">Oryginalna muzyka instrumentalna Lyria. Każda generacja to około 30 sekund i <strong>0,04 USD</strong>. Utwór trafia do biblioteki jako szkic i nie może być użyty na live bez zatwierdzenia praw.</p>
        <div className="nb-grid nb-grid--2">
          <Field label="Styl">
            <select className="select" value={profileId} onChange={(event) => setProfileId(event.target.value)}>
              {profiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
            </select>
          </Field>
          <Field label="Dodatkowe wskazówki (opcjonalnie)">
            <input className="input" value={prompt} onChange={(event) => setPrompt(event.target.value)} maxLength={1200} placeholder="np. bardzo spokojnie, bez perkusji" />
          </Field>
        </div>
        <div className="btn-row" style={{ marginTop: 12 }}>
          <Button
            variant="primary"
            disabled={busy === 'generate' || !profileId || settings?.enabled !== true}
            onClick={() => {
              if (!window.confirm('Wygenerować 30-sekundowy utwór? Szacowany koszt: 0,04 USD.')) return;
              void run('generate', () => api.nieboguard.music.generate({ profileId, prompt: prompt.trim() || undefined }), 'Utwór wygenerowany i zapisany jako szkic do review.');
            }}
          >{busy === 'generate' ? 'Generuję…' : 'Generuj utwór (0,04 USD)'}</Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={!settings || busy === 'settings'}
            onClick={() => run('settings', () => api.nieboguard.updateSettings({ music: { enabled: !settings?.enabled } }), settings?.enabled ? 'Music Studio wyłączone.' : 'Music Studio włączone.')}
          >{settings?.enabled ? 'Wyłącz generowanie' : 'Włącz generowanie'}</Button>
        </div>
        <p className="nb-stat-note">Budżet dzienny: {settings?.dailyBudgetUsd ?? 0.24} USD. Model: {settings?.model ?? 'google/lyria-3-clip-preview'}.</p>
      </Card>

      {audioSettings ? <Card title="Audio Engine — podkład live" actions={<Badge tone={audioSettings.enabled ? 'success' : 'warning'}>{audioSettings.enabled ? 'włączony' : 'wyłączony'}</Badge>}>
        <p className="nb-stat-note">Do transmisji trafiają wyłącznie utwory zatwierdzone z potwierdzonymi prawami. Worker pobiera playlistę i miksuje ją pod mapą; przy braku utworów wraca do ciszy. Zmiana jest stosowana po restarcie sesji.</p>
        <label className="nb-row"><input type="checkbox" checked={audioSettings.enabled === true} onChange={(event) => setAudioSettings({ ...audioSettings, enabled: event.target.checked })} /> Włącz muzykę w live</label>
        <label className="nb-row"><input type="checkbox" checked={audioSettings.identEnabled !== false} onChange={(event) => setAudioSettings({ ...audioSettings, identEnabled: event.target.checked })} /> Dodaj identyfikację stacji Piper na początku pętli</label>
        <Field label="Tryb playlisty">
          <select className="select" value={audioSettings.mode ?? 'AUTO'} onChange={(event) => setAudioSettings({ ...audioSettings, mode: event.target.value })}>
            <option value="AUTO">AUTO — dzień / noc</option>
            <option value="NORMAL">NORMAL</option>
            <option value="NIGHT">NIGHT</option>
          </select>
        </Field>
        <label className="nb-row"><input type="checkbox" checked={audioSettings.alertsEnabled === true} onChange={(event) => setAudioSettings({ ...audioSettings, alertsEnabled: event.target.checked })} /> Czytaj nowe ważne alerty przez Piper (jeden kontrolowany restart audio)</label>
        <Field label="Głośność muzyki (0.02–0.6)"><input className="input" type="number" min={0.02} max={0.6} step={0.01} value={audioSettings.musicVolume ?? 0.18} onChange={(event) => setAudioSettings({ ...audioSettings, musicVolume: Number(event.target.value) })} /></Field>
        <Button size="sm" variant="primary" style={{ marginTop: 12 }} disabled={busy === 'audio-settings'} onClick={() => run('audio-settings', () => api.nieboguard.updateSettings({ audio: audioSettings }), 'Ustawienia Audio Engine zapisane.')}>Zapisz Audio Engine</Button>
      </Card> : null}

      <Card title="Playlisty NG TV" actions={<Button size="sm" variant="primary" disabled={busy === 'playlist-create' || playlistName.trim().length < 3} onClick={() => run('playlist-create', async () => { await api.nieboguard.music.createPlaylist({ name: playlistName.trim() }); setPlaylistName(''); }, 'Playlista utworzona.')}>Utwórz playlistę</Button>}>
        <div className="nb-row" style={{ marginBottom: 10 }}>
          <input className="input" value={playlistName} onChange={(event) => setPlaylistName(event.target.value)} placeholder="np. Night Ambient" />
        </div>
        <div className="nb-list">
          {playlists.map((playlist) => (
            <div className="nb-item nb-row nb-row--between" key={playlist.id}>
              <div>
                <div className="nb-item__title">{playlist.name}</div>
                <div className="nb-item__meta"><Badge tone="neutral">{playlist.mode}</Badge><span>{playlist.tracks?.length ?? 0} utworów</span><span>{playlist.status}</span></div>
              </div>
              <div className="btn-row">
                <Button size="sm" variant={audioSettings?.playlistId === playlist.id ? 'primary' : 'ghost'} onClick={() => run(`playlist-${playlist.id}`, () => api.nieboguard.updateSettings({ audio: { playlistId: playlist.id } }), 'Playlista ustawiona jako aktywna.')}>Aktywuj</Button>
                <Button size="sm" variant="ghost" onClick={() => run(`playlist-archive-${playlist.id}`, () => api.nieboguard.music.archivePlaylist(playlist.id), 'Playlista zarchiwizowana.')}>Archiwum</Button>
              </div>
            </div>
          ))}
          {playlists.length === 0 ? <EmptyState title="Brak playlist" hint="Utwórz playlistę z zatwierdzonych utworów." /> : null}
        </div>
        <p className="nb-stat-note">Nowa playlista automatycznie zbiera wszystkie zatwierdzone utwory. Aktywna playlista jest używana po restarcie sesji.</p>
      </Card>

      <Card title="Biblioteka utworów">
        <div className="nb-list">
          {tracks.map((track) => (
            <div className="nb-item nb-row nb-row--between" key={track.id}>
              <div>
                <div className="nb-item__title">{track.title}</div>
                <div className="nb-item__meta">
                  <Badge tone={track.status === 'APPROVED' ? 'success' : track.status === 'REJECTED' ? 'danger' : 'warning'}>{track.status}</Badge>
                  <span>{track.category}</span><span>{track.durationSeconds}s</span><span>{track.costUsd?.toFixed?.(2)} USD</span>
                  <span>prawa: {track.rightsStatus}</span>
                </div>
                {track.sourceUrl ? <audio controls preload="none" src={track.sourceUrl} style={{ width: '100%', marginTop: 8 }} /> : null}
              </div>
              <div className="btn-row">
                <Button size="sm" variant="primary" disabled={track.status === 'APPROVED' || busy === `approve-${track.id}`} onClick={() => {
                  if (!window.confirm('Potwierdzasz prawa do komercyjnej emisji i transmisji live tego utworu?')) return;
                  void run(`approve-${track.id}`, () => api.nieboguard.music.approve(track.id), 'Utwór zatwierdzony do emisji.');
                }}>Zatwierdź</Button>
                <Button size="sm" variant="ghost" disabled={track.status === 'REJECTED' || busy === `reject-${track.id}`} onClick={() => run(`reject-${track.id}`, () => api.nieboguard.music.reject(track.id), 'Utwór odrzucony.')}>Odrzuć</Button>
              </div>
            </div>
          ))}
          {tracks.length === 0 ? <EmptyState title="Brak utworów" hint="Wygeneruj pierwszy utwór lub zaimportuj gotowe nagranie." /> : null}
        </div>
      </Card>

      <Card title="Historia generacji">
        <div className="nb-list">
          {generations.slice(0, 10).map((generation) => (
            <div className="nb-item" key={generation.id}>
              <div className="nb-item__title">{generation.title}</div>
              <div className="nb-item__meta"><Badge tone={generation.status === 'FAILED' ? 'danger' : generation.status === 'READY_FOR_REVIEW' ? 'success' : 'warning'}>{generation.status}</Badge><span>{generation.category}</span><span>{generation.estimated_cost_usd} USD</span><span>{generation.created_at}</span></div>
              {generation.error_code ? <div className="nb-stat-note">{generation.error_code}</div> : null}
            </div>
          ))}
          {generations.length === 0 ? <EmptyState title="Brak generacji" hint="Historia pojawi się po pierwszym uruchomieniu." /> : null}
        </div>
      </Card>
    </>
  );
}
